import { Link } from 'react-router-dom';
import { Clock, MoreHorizontal, Trash2, Cpu, MemoryStick } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { Sandbox } from '@/types';
import { formatProcessorCount, formatMemorySize, getTimeRemaining } from '@/utils/format';

interface SandboxCardProps {
  sandbox: Sandbox;
  onDelete?: (sandboxId: string, name: string) => void;
  showDelete?: boolean;
}

/** State-based gradient for card header */
function getHeaderGradient(state: string): string {
  switch (state) {
    case 'Running':
      return 'from-brand-600 via-blue-500 to-cyan-400';
    case 'Creating':
      return 'from-amber-500 via-orange-400 to-yellow-300';
    case 'Failed':
      return 'from-red-800 via-red-700 to-red-500';
    case 'Deleting':
      return 'from-surface-600 via-surface-500 to-surface-400';
    default:
      return 'from-surface-700 via-surface-600 to-surface-500';
  }
}

/** State dot color */
function getStateDot(state: string): string {
  switch (state) {
    case 'Running':  return 'bg-green-400';
    case 'Creating': return 'bg-amber-400 animate-pulse';
    case 'Failed':   return 'bg-red-400';
    case 'Deleting': return 'bg-surface-400 animate-pulse';
    default:         return 'bg-surface-400';
  }
}

export function SandboxCard({ sandbox, onDelete, showDelete }: SandboxCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const remaining = getTimeRemaining(sandbox.expiresAt);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <div className="group relative">
      <Link
        to={`/sandboxes/${sandbox.sandboxId}`}
        className={clsx(
          'block rounded-fluent-lg overflow-hidden transition-all duration-200',
          'bg-white dark:bg-[#2a2a2a]',
          'border border-surface-200 dark:border-white/[0.06]',
          'hover:border-surface-300 dark:hover:border-white/[0.12]',
          'hover:shadow-elevation-8 dark:hover:shadow-glow-brand-sm',
          'hover:scale-[1.02]'
        )}
      >
        {/* Gradient header */}
        <div className={clsx(
          'h-24 bg-gradient-to-br relative overflow-hidden',
          getHeaderGradient(sandbox.state)
        )}>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 40%)',
            }}
          />

          {/* State badge */}
          <div className="absolute top-3 left-3">
            <span className={clsx(
              'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium',
              'bg-black/30 text-white backdrop-blur-sm'
            )}>
              <span className={clsx('w-1.5 h-1.5 rounded-full', getStateDot(sandbox.state))} />
              {sandbox.state}
            </span>
          </div>

          {/* Expiry in header */}
          {remaining && (
            <div className="absolute top-3 right-3">
              <span className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium',
                'bg-black/30 text-white backdrop-blur-sm',
                remaining.isUrgent && 'text-red-200'
              )}>
                <Clock className="w-3 h-3" />
                {remaining.display}
              </span>
            </div>
          )}
        </div>

        {/* Card body */}
        <div className="p-3.5">
          <h3 className="text-sm font-semibold text-surface-900 dark:text-surface-100 truncate mb-1">
            {sandbox.sandboxName}
          </h3>
          <p className="text-xs text-surface-500 dark:text-surface-400 truncate mb-3 font-mono">
            {sandbox.imageId}
          </p>

          {/* Specs row */}
          <div className="flex items-center gap-3 text-xs text-surface-500 dark:text-surface-400">
            <span className="inline-flex items-center gap-1">
              <Cpu className="w-3 h-3" />
              {formatProcessorCount(sandbox.shape.processorCount)}
            </span>
            <span className="inline-flex items-center gap-1">
              <MemoryStick className="w-3 h-3" />
              {formatMemorySize(sandbox.shape.memorySize)}
            </span>
          </div>
        </div>
      </Link>

      {/* Context menu button */}
      {showDelete && (sandbox.state === 'Running' || sandbox.state === 'Failed') && (
        <div ref={menuRef} className="absolute bottom-3.5 right-3">
          <button
            onClick={(e) => { e.preventDefault(); setMenuOpen(!menuOpen); }}
            className={clsx(
              'p-1 rounded-fluent transition-all duration-150',
              'text-surface-400 hover:text-surface-600 dark:hover:text-surface-200',
              'hover:bg-surface-100 dark:hover:bg-white/[0.08]',
              'opacity-0 group-hover:opacity-100',
              menuOpen && 'opacity-100'
            )}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {menuOpen && (
            <div className={clsx(
              'absolute bottom-full right-0 mb-1 w-36 z-50',
              'bg-white dark:bg-[#2a2a2a] rounded-fluent-md',
              'border border-surface-200 dark:border-white/[0.08]',
              'shadow-elevation-8 dark:shadow-none',
              'py-1 animate-fade-in'
            )}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setMenuOpen(false);
                  onDelete?.(sandbox.sandboxId, sandbox.sandboxName);
                }}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Terminate
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
